import { CircularProgress, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { type FC, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import type { Database } from "../../../../../__definitions__/database-definitions";
import { useApiServiceCallback } from "../../../../../server/api/client";
import { Refresh } from "../../../../../styles/icons/refresh";
import { CustomEmailDomainsService } from "../../client/custom-email-domains-service";
import type { CustomEmailDomain } from "../../server/supabase-custom-email-domain-service";

export const RefreshButton: FC<{
  customEmailDomain: CustomEmailDomain;
  onVerified: () => void;
}> = ({ customEmailDomain, onVerified }) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);

  const handleCheckStatus = useApiServiceCallback(
    CustomEmailDomainsService,
    async (
      { apiService },
      emailDomain: Database["public"]["Tables"]["custom_email_domain"]["Row"],
    ): Promise<void> => {
      setRefreshing(true);

      const { data } = await apiService.refreshVerifiedStatus(emailDomain.id);

      if (data) {
        if (data.verified) {
          toast.success(t("{{domain}} has been verified.", { domain: emailDomain.domain }));
          onVerified();
        } else {
          toast(t("{{domain}} has not yet been verified.", { domain: emailDomain.domain }), {
            icon: "⚠️",
          });
          setRefreshing(false);
        }
      } else {
        toast.error(
          t("Something went wrong when refreshing status for {{domain}}", {
            domain: emailDomain.domain,
          }),
        );
        setRefreshing(false);
      }
    },
    [onVerified, t],
  );

  return (
    <GridActionsCellItem
      key="refresh"
      icon={
        <Tooltip
          title={t("Refresh Status for {{domain}}", {
            domain: customEmailDomain.domain,
          })}
          placement="right"
        >
          {refreshing ? <CircularProgress size="small" /> : <Refresh fontSize="small" />}
        </Tooltip>
      }
      label={t("Refresh Status for {{domain}}", { domain: customEmailDomain.domain })}
      aria-label={`Refresh Status for ${customEmailDomain.domain}`}
      showInMenu={false}
      onClick={() => {
        void handleCheckStatus(customEmailDomain).catch(console.error);
      }}
    />
  );
};
